<template>
  <div class="page-content-wrapper">
    <div class="card shadow rounded-3 border-1">
      <div
        class="card-header bg-white border-0 me-2 d-flex justify-content-between align-items-center"
      >
        <p class="text-s fw-bold">{{ service_name }}</p>
      </div>
      <div class="card-body">
        <CustomTabs
          :tabs="[
            { label: 'Questions', value: 'form' },
            {
              label: 'Rules',
              value: 'rules',
              disabled: !hasAssignedQuestions(),
            },
          ]"
          v-model="activeTab"
        />
        <div class="tab-content">
          <div v-show="activeTab === 'form'">
            <div v-if="loading" class="text-center mt-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div v-else>
              <CustomTable
                :columns="questionsColumns"
                :fetchData="get_active_questions"
                :hasActions="false"
                tableKey="active_questions"
              >
                <template v-slot:cell-question="{ row }">
                  <span class="text-xxs">{{ row.question }}</span>
                </template>
                <template v-slot:cell-question_type="{ row }">
                  <span class="text-xxs">{{ row.question_type }}</span>
                </template>
                <template v-slot:cell-assign_question="{ row }">
                  <span>
                    <input
                      class="form-check-input checkbox-shadow"
                      type="checkbox"
                      :id="'check' + row.id"
                      :value="row.id"
                      v-model="assigned_question_ids"
                      @change="assign_question(row.id, $event.target.checked)"
                    />
                  </span>
                </template>
              </CustomTable>
              <div class="accordion mt-4" id="assigned_questions_accordion">
                <div class="accordion-item border-0 mb-2">
                  <button
                    class="btn border border-2 rounded-3 fw-bold text-xs shadow-sm d-flex align-items-center w-100 py-2 mb-2"
                    :class="{
                      'bg-primary-color': isAssignedQuestionsOpen,
                      'bg-white': !isAssignedQuestionsOpen,
                    }"
                    type="button"
                    @click="isAssignedQuestionsOpen = !isAssignedQuestionsOpen"
                    aria-expanded="isAssignedQuestionsOpen"
                    aria-controls="assigned_questions_collapse"
                  >
                    <span class="text-dark">Assigned Questions</span>
                    <span class="ms-auto text-dark">
                      <i
                        :class="
                          isAssignedQuestionsOpen
                            ? 'fa fa-chevron-up'
                            : 'fa fa-chevron-down'
                        "
                      ></i>
                    </span>
                  </button>
                  <div
                    id="assigned_questions_collapse"
                    v-show="isAssignedQuestionsOpen"
                    class="accordion-body bg-light rounded-small"
                  >
                    <div class="mb-2 text-muted text-xxs">
                      <i class="fas fa-info-circle me-1"></i>
                      You can drag and drop to reorder your assigned questions.
                    </div>
                    <draggable
                      v-model="ordered_questions"
                      handle=".drag-handle"
                      tag="ul"
                      class="list-group"
                      item-key="id"
                    >
                      <template #item="{ element, index }">
                        <li
                          class="list-group-item d-flex align-items-center border-bottom"
                        >
                          <span
                            class="drag-handle me-2"
                            style="cursor: grabbing"
                          >
                            <i class="fas fa-bars fa-sm"></i>
                          </span>
                          <span>{{ index + 1 }}. {{ element.question }}</span>
                        </li>
                      </template>
                    </draggable>
                    <div
                      class="d-flex justify-content-end mt-3"
                      v-if="ordered_questions.length > 0"
                    >
                      <button
                        class="btn bg-green rounded-3 text-white text-xs fw-bold"
                        @click="save_ordered_questions"
                        :disabled="!is_order_changed"
                      >
                        Save Changes
                      </button>
                    </div>
                    <div
                      v-if="ordered_questions.length === 0"
                      class="text-center text-muted mt-3"
                    >
                      No assigned questions to display.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Updated Rules Tab Content with Weight Button and Percentage Display -->
          <div v-show="activeTab === 'rules'">
            <p class="fw-bold text-s mb-1">Question Settings</p>
            <div class="tab-content">
              <div v-if="loading">
                <div class="text-center my-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
              <div v-else>
                <CustomTable
                  :columns="rulesColumns"
                  :fetchData="rulesTableData"
                  :key="rulesTableKey"
                  :hasActions="false"
                  :showSearch="false"
                >
                  <template v-slot:cell-question="{ row }">
                    <span class="text-xs text-start">{{ row.question }}</span>
                  </template>
                  <template v-slot:cell-question_type="{ row }">
                    <span class="text-xs text-start">{{
                      row.question_type
                    }}</span>
                  </template>
                  <template v-slot:cell-score="{ row }">
                    <div
                      v-if="
                        row.question_type !== 'Text' &&
                        row.question_type !== 'File'
                      "
                    >
                      <button
                        class="btn bg-green rounded-3 btn-sm text-white text-xxs"
                        @click="openWeightModal(row.id)"
                      >
                        Set Score
                      </button>
                      <span
                        class="text-nowrap text-muted d-inline-block w-25 ms-1"
                      >
                        <template
                          v-if="row.weight && parseInt(row.weight) != 0"
                        >
                          {{ parseInt(row.weight) }}
                          <template
                            v-if="getWeightPercentage(row.id) % 1 !== 0"
                          >
                            ({{ getWeightPercentage(row.id).toFixed(1) }}%)
                          </template>
                          <template v-else>
                            ({{ Math.round(getWeightPercentage(row.id)) }}%)
                          </template>
                        </template>
                        <template v-else> &nbsp; </template>
                      </span>
                    </div>
                  </template>
                  <template v-slot:cell-rules="{ row }">
                    <div
                      class="position-relative"
                      v-if="
                        row.question_type !== 'Text' &&
                        row.question_type !== 'File'
                      "
                    >
                      <div @click="handleButtonClick(row)">
                        <button
                          class="btn btn-dark rounded-3 text-white fw-bold btn-sm text-xxs shadow"
                          :class="{
                            'disabled-button':
                              !row.weight || parseInt(row.weight) == 0,
                          }"
                          :disabled="!row.weight || parseInt(row.weight) == 0"
                        >
                          <i class="fas fa-cog me-1"></i>Set Rule
                        </button>
                        <div v-if="row.showTooltip" class="alert-tooltip">
                          Set score first
                        </div>
                      </div>
                    </div>
                  </template>
                </CustomTable>
              </div>
            </div>

            <hr class="border-0" />
            <p class="fw-bold text-s mb-1">Overall Severity Thresholds</p>
            <div class="tab-content">
              <CustomTable
                :columns="thresholdsColumns"
                :fetchData="thresholdsTableData"
                :hasActions="true"
                :showSearch="false"
                :key="thresholdsTableKey"
              >
                <template v-slot:cell-level="{ row }">
                  <span class="text-xs text-start">{{ row.level }}</span>
                </template>
                <template v-slot:cell-threshold="{ row }">
                  <span class="text-xs text-start">{{ row.threshold }}</span>
                </template>
                <template v-slot:actions="{ row }">
                  <button
                    @click="openModal(row.key)"
                    class="btn bg-green rounded-3 btn-md me-2 text-white text-xs"
                  >
                    <i class="fas fa-edit me-1"></i>Edit
                  </button>
                </template>
              </CustomTable>
            </div>

            <!-- Enhanced Professional Threshold Edit Modal -->
            <div
              v-if="showThresholdModal"
              class="modal-backdrop fade show"
              @click="closeThresholdModal"
            ></div>
            <div
              v-if="showThresholdModal"
              class="modal fade show d-block"
              tabindex="-1"
              role="dialog"
              aria-labelledby="thresholdModalLabel"
              aria-hidden="true"
            >
              <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content shadow" @click.stop>
                  <div
                    class="modal-header bg-dark text-white d-flex align-items-center"
                  >
                    <h5 class="modal-title" id="thresholdModalLabel">
                      <i class="fas fa-sliders-h me-2"></i>{{ modalTitle }}
                    </h5>
                    <button
                      type="button"
                      class="btn-close btn-close-white"
                      @click="closeThresholdModal"
                      aria-label="Close"
                    ></button>
                  </div>
                  <div class="modal-body">
                    <form @submit.prevent="saveThreshold">
                      <!-- Total Weight Summary Card -->
                      <div class="card mb-4 bg-light border shadow-sm">
                        <div class="card-body">
                          <h6 class="card-title d-flex align-items-center mb-3">
                            <i class="fas fa-calculator me-2 text-primary"></i>
                            <span>Total Score Summary</span>
                          </h6>
                          <div
                            class="d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <p class="mb-1">
                                Total Question Score:
                                <span class="fw-bold">{{
                                  getTotalWeight()
                                }}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="currentLevel === 'high'"
                        class="border rounded p-3 mb-3 bg-light shadow-sm"
                      >
                        <div class="d-flex align-items-center mb-3">
                          <span
                            class="badge me-2 p-2"
                            :style="{
                              backgroundColor: getColorForThreshold('high'),
                            }"
                            >High</span
                          >
                          <h6 class="mt-2 mb-1 fw-bold">
                            High Severity Threshold
                          </h6>
                        </div>

                        <p class="text-muted small mb-3">
                          Define when a form should be flagged as high severity.
                          This typically indicates critical issues requiring
                          immediate attention.
                        </p>

                        <div class="d-flex align-items-center">
                          <div class="input-group me-3 w-25">
                            <select
                              v-model="editThreshold.operator"
                              class="form-select border-start-0"
                              @change="validateThresholds"
                            >
                              <option value="≥">
                                ≥ (Greater than or equal)
                              </option>
                              <option value=">">> (Greater than)</option>
                            </select>
                          </div>

                          <div class="input-group w-130">
                            <input
                              type="number"
                              :value="editThreshold.value"
                              class="form-control text-center fw-bold"
                              min="0"
                              max="100"
                              required
                              @input="
                                validateThresholdsValidation(
                                  $event,
                                  'editThreshold'
                                )
                              "
                            />
                            <span class="input-group-text bg-light">%</span>
                          </div>

                          <div class="ms-3 weight-display">
                            <span class="badge bg-secondary">
                              ≈
                              {{
                                Math.round(
                                  (editThreshold.value / 100) * getTotalWeight()
                                )
                              }}
                              points
                            </span>
                          </div>
                        </div>

                        <div class="mt-3 text-end">
                          <small class="text-muted fst-italic"
                            >Value must be between 0-100%</small
                          >
                          <div
                            v-if="thresholdError"
                            class="text-danger small mt-1"
                          >
                            {{ thresholdError }}
                          </div>
                        </div>
                      </div>

                      <!-- Medium Severity Form -->
                      <div
                        v-if="currentLevel === 'medium'"
                        class="border rounded p-3 mb-3 bg-light shadow-sm"
                      >
                        <div class="d-flex align-items-center mb-3">
                          <span
                            class="badge text-dark me-2 p-2"
                            :style="{
                              backgroundColor: getColorForThreshold('medium'),
                            }"
                            >Medium</span
                          >
                          <h6 class="mt-2 mb-1 fw-bold">
                            Medium Severity Range
                          </h6>
                        </div>

                        <p class="text-muted small mb-3">
                          Define the range for medium severity assessment items.
                          These typically indicate issues that need attention
                          but aren't critical.
                        </p>

                        <div class="mb-3">
                          <label class="form-label small text-muted mb-1"
                            >Range start:</label
                          >
                          <div class="d-flex align-items-center">
                            <div class="input-group me-3 w-25">
                              <select
                                v-model="editThreshold.lowerOperator"
                                class="form-select border-start-0"
                                @change="validateThresholds"
                              >
                                <option value="≥">
                                  ≥ (Greater than or equal)
                                </option>
                                <option value=">">> (Greater than)</option>
                              </select>
                            </div>
                            <div class="input-group w-130">
                              <input
                                type="number"
                                v-model.number="editThreshold.lowerValue"
                                class="form-control text-center fw-bold"
                                min="0"
                                max="100"
                                required
                                @input="validateThresholds"
                              />
                              <span class="input-group-text bg-light">%</span>
                            </div>
                            <div class="ms-3 weight-display">
                              <span class="badge bg-secondary">
                                ≈
                                {{
                                  Math.round(
                                    (editThreshold.lowerValue / 100) *
                                      getTotalWeight()
                                  )
                                }}
                                points
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label class="form-label small text-muted mb-1"
                            >Range end:</label
                          >
                          <div class="d-flex align-items-center">
                            <div class="input-group me-3 w-25">
                              <select
                                v-model="editThreshold.upperOperator"
                                class="form-select border-start-0"
                                @change="validateThresholds"
                              >
                                <option value="<">&lt; (Less than)</option>
                                <option value="≤">
                                    ≤ (Less than or equal)
                                </option>
                              </select>
                            </div>
                            <div class="input-group w-130">
                              <input
                                type="number"
                                v-model.number="editThreshold.upperValue"
                                class="form-control text-center fw-bold"
                                min="0"
                                max="100"
                                required
                                @input="validateThresholds"
                              />
                              <span class="input-group-text bg-light">%</span>
                            </div>
                            <div class="ms-3 weight-display">
                              <span class="badge bg-secondary">
                                ≈
                                {{
                                  Math.round(
                                    (editThreshold.upperValue / 100) *
                                      getTotalWeight()
                                  )
                                }}
                                points
                              </span>
                            </div>
                          </div>
                        </div>

                        <div class="mt-3 text-end">
                          <small class="text-muted fst-italic"
                            >Values must be between 0-100%</small
                          >
                          <div
                            v-if="thresholdError"
                            class="text-danger small mt-1"
                          >
                            {{ thresholdError }}
                          </div>
                        </div>
                      </div>

                      <!-- Low Severity Form -->
                      <div
                        v-if="currentLevel === 'low'"
                        class="border rounded p-3 mb-3 bg-light shadow-sm"
                      >
                        <div class="d-flex align-items-center mb-3">
                          <span
                            class="badge me-2 p-2"
                            :style="{
                              backgroundColor: getColorForThreshold('low'),
                            }"
                            >Low</span
                          >
                          <h6 class="mt-2 mb-1 fw-bold">
                            Low Severity Threshold
                          </h6>
                        </div>

                        <p class="text-muted small mb-3">
                          Define when an assessment item should be categorized
                          as low severity. These typically indicate minor issues
                          or acceptable conditions.
                        </p>

                        <div class="d-flex align-items-center">
                          <div class="input-group me-3 w-25">
                            <select
                              v-model="editThreshold.operator"
                              class="form-select border-start-0"
                              @change="validateThresholds"
                            >
                              <option value="<">&lt; (Less than)</option>
                            </select>
                          </div>
                          <div class="input-group w-130">
                            <input
                              type="number"
                              v-model.number="editThreshold.value"
                              class="form-control text-center fw-bold"
                              min="0"
                              max="100"
                              required
                              @input="validateThresholds"
                            />
                            <span class="input-group-text bg-light">%</span>
                          </div>
                          <div class="ms-3 weight-display">
                            <span class="badge bg-secondary">
                              ≈
                              {{
                                Math.round(
                                  (editThreshold.value / 100) * getTotalWeight()
                                )
                              }}
                              points
                            </span>
                          </div>
                        </div>

                        <div class="mt-3 text-end">
                          <small class="text-muted fst-italic"
                            >Value must be between 0-100%</small
                          >
                          <div
                            v-if="thresholdError"
                            class="text-danger small mt-1"
                          >
                            {{ thresholdError }}
                          </div>
                        </div>
                      </div>

                      <!-- Preview section showing current configuration with dynamic colors -->
                      <div class="card mt-4 bg-white border shadow-sm">
                        <div class="card-body">
                          <div class="d-flex justify-content-center mb-3">
                            <div class="threshold-preview">
                              <div class="range-bar">
                                <div
                                  class="range-segment"
                                  :style="{
                                    backgroundColor:
                                      getColorForThreshold('low'),
                                    width: '33%',
                                  }"
                                >
                                  <span class="range-label">Low</span>
                                </div>
                                <div
                                  class="range-segment"
                                  :style="{
                                    backgroundColor:
                                      getColorForThreshold('medium'),
                                    width: '34%',
                                  }"
                                >
                                  <span
                                    class="range-label"
                                    :class="{ 'text-dark': isMediumLight }"
                                    >Medium</span
                                  >
                                </div>
                                <div
                                  class="range-segment"
                                  :style="{
                                    backgroundColor:
                                      getColorForThreshold('high'),
                                    width: '33%',
                                  }"
                                >
                                  <span class="range-label">High</span>
                                </div>
                              </div>
                              <div class="range-scale">
                                <span>0%</span>
                                <span
                                  >{{
                                    thresholds.low.value ||
                                    editThreshold.value ||
                                    50
                                  }}%</span
                                >
                                <span
                                  >{{
                                    thresholds.medium.upperValue ||
                                    editThreshold.upperValue ||
                                    75
                                  }}%</span
                                >
                                <span>100%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                  <div class="modal-footer bg-light">
                    <button
                      type="button"
                      class="btn bg-danger rounded-small btn-lg me-2 text-white text-sm"
                      @click="closeThresholdModal"
                    >
                      <i class="fas fa-times me-1"></i>Cancel
                    </button>
                    <button
                      type="button"
                      class="btn bg-green rounded-small btn-lg me-2 text-white text-sm"
                      @click="saveThreshold"
                      :disabled="thresholdError"
                    >
                      <i class="fas fa-save me-1"></i>Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showConfigModal"
    class="modal-backdrop fade show"
    @click="closeConfigModal"
  ></div>
  <div
    v-if="showConfigModal"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-labelledby="configModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <!-- Modal Header with improved styling -->
        <div class="modal-header bg-dark text-white d-flex align-items-center">
          <h5 class="modal-title fs-5" id="configModalLabel">
            <i class="fas fa-cog me-2"></i>Rules Setup
          </h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            @click="closeConfigModal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body p-4">
          <!-- Question Display with Weight - Improved card design -->
          <div class="card mb-4 shadow-sm border-0">
            <div
              class="card-header bg-light d-flex justify-content-between align-items-center py-3"
            >
              <h6 class="mb-0 fw-bold text-dark">
                <i class="fas fa-question-circle me-2 text-primary"></i>Question
                Details
              </h6>
              <span class="badge bg-dark rounded-pill">
                Score: {{ selectedQuestion.weight || 0 }} ({{
                  getWeightPercentage(selectedQuestion.id).toFixed(1)
                }}%)
              </span>
            </div>
            <div class="card-body p-3">
              <p class="mb-2 fs-6">{{ selectedQuestion.question }}</p>
              <div class="d-flex align-items-center">
                <span class="badge bg-light text-dark me-2">
                  <i class="fas fa-tag me-1"></i
                  >{{ selectedQuestion.question_type }}
                </span>
              </div>
            </div>
          </div>

          <!-- Rules Configuration - Enhanced styling -->
          <div class="card shadow-sm border-0">
            <div
              class="card-header bg-light d-flex justify-content-between align-items-center py-3"
            >
              <h6 class="mb-0 fw-bold text-dark">
                <i class="fas fa-list-check me-2 text-primary"></i>Rules
              </h6>
              <button
                class="btn bg-green rounded-small btn-sm me-2 text-white text-xxs"
                @click="addNewRule"
                :disabled="selectedQuestion.rules && isMaxRulesReached()"
              >
                <i class="fas fa-plus me-1"></i> Add Rule
              </button>
            </div>

            <div class="card-body p-3">
              <div
                v-if="
                  selectedQuestion.rules && selectedQuestion.rules.length > 0
                "
              >
                <!-- Rules List with improved styling -->
                <div
                  v-for="(rule, index) in selectedQuestion.rules"
                  :key="index"
                  class="rule-item border rounded p-3 mb-3 position-relative bg-light shadow-sm"
                >
                  <div
                    class="rule-header d-flex justify-content-between align-items-center mb-3"
                  >
                    <span
                      class="badge"
                      :class="{
                        'bg-danger': rule.severity === 'high',
                        'bg-warning text-dark': rule.severity === 'medium',
                        'bg-success': rule.severity === 'low',
                      }"
                    >
                      <i
                        class="fas"
                        :class="{
                          'fa-exclamation-triangle': rule.severity === 'high',
                          'fa-info-circle': rule.severity === 'medium',
                          'fa-check-circle': rule.severity === 'low',
                        }"
                      ></i>
                      {{
                        rule.severity.charAt(0).toUpperCase() +
                        rule.severity.slice(1)
                      }}
                      Severity
                    </span>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="removeRule(index)"
                    >
                      <i class="fas fa-trash-alt me-1"></i>Remove
                    </button>
                  </div>

                  <div class="row g-3 align-items-center mb-3">
                    <div class="col-md-2">
                      <select
                        class="form-select form-select-sm severity-select"
                        v-model="rule.severity"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>

                  <div class="row g-3 align-items-center">
                    <!-- Condition Operator - Dynamic based on question type -->
                    <div class="col-md-3">
                      <label
                        class="form-label fw-semibold mb-2 d-flex justify-content-between align-items-center"
                      >
                        <span
                          ><i class="fas fa-code-branch me-1"></i
                          >Condition</span
                        >
                      </label>
                      <select
                        class="form-select form-select-sm"
                        v-model="rule.operator"
                      >
                        <!-- Numeric operators -->
                        <option v-if="showNumericOperators()" value="<">
                          Less than (&lt;)
                        </option>
                        <option v-if="showNumericOperators()" value="<=">
                          Less than or equal (≤)
                        </option>
                        <option
                          v-if="showNumericOperators() || showSelectOperators()"
                          value="=="
                        >
                          Equal to (=)
                        </option>
                        <option v-if="showNumericOperators()" value=">=">
                          Greater than or equal (≥)
                        </option>
                        <option v-if="showNumericOperators()" value=">">
                          Greater than (&gt;)
                        </option>

                        <!-- Text operators -->
                        <option v-if="showTextOperators()" value="contains">
                          Contains
                        </option>
                        <option v-if="showTextOperators()" value="not_contains">
                          Does not contain
                        </option>
                        <option v-if="showTextOperators()" value="starts_with">
                          Starts with
                        </option>
                        <option v-if="showTextOperators()" value="ends_with">
                          Ends with
                        </option>
                        <option v-if="showTextOperators()" value="==">
                          Exactly matches
                        </option>

                        <!-- Boolean operators -->
                        <option v-if="showBooleanOperators()" value="is_true">
                          Is True/Yes
                        </option>
                        <option v-if="showBooleanOperators()" value="is_false">
                          Is False/No
                        </option>

                        <!-- Date operators -->
                        <option v-if="showDateOperators()" value="<">
                          Before
                        </option>
                        <option v-if="showDateOperators()" value=">">
                          After
                        </option>
                        <option v-if="showDateOperators()" value="==">
                          On
                        </option>
                        <option v-if="showDateOperators()" value="Today" hidden>
                          Today
                        </option>
                      </select>
                    </div>

                    <!-- Dynamic Value Input Based on Question Type -->
                    <div
                      v-bind:class="[
                        selectedQuestion.weight && selectedQuestion.weight > 0
                          ? 'col-md-5'
                          : 'col-md-9',
                      ]"
                    >
                      <label class="form-label fw-semibold mb-2">
                        <i class="fas fa-check-circle me-1"></i>Value
                      </label>

                      <!-- For Number type questions -->
                      <input
                        v-if="selectedQuestion.question_type === 'Number'"
                        type="number"
                        class="form-control form-control-sm"
                        v-model="rule.value"
                        placeholder="Enter numeric value"
                      />

                      <!-- For Date type questions - with type radio toggle -->
                      <div
                        v-else-if="selectedQuestion.question_type === 'Date'"
                        class="date-input-container"
                      >
                        <div class="mb-2 bg-white p-2 rounded border">
                          <div class="form-check form-check-inline mb-0">
                            <input
                              class="form-check-input"
                              type="radio"
                              :name="'dateType-' + index"
                              :id="'dateType-calendar-' + index"
                              v-model="rule.dateType"
                              value="calendar"
                            />
                            <label
                              class="form-check-label"
                              :for="'dateType-calendar-' + index"
                              >Calendar</label
                            >
                          </div>
                          <div class="form-check form-check-inline mb-0">
                            <input
                              class="form-check-input"
                              type="radio"
                              :name="'dateType-' + index"
                              :id="'dateType-number-' + index"
                              v-model="rule.dateType"
                              value="number"
                            />
                            <label
                              class="form-check-label"
                              :for="'dateType-number-' + index"
                              >Days</label
                            >
                          </div>
                          <div class="form-check form-check-inline mb-0">
                            <input
                              class="form-check-input"
                              type="radio"
                              :name="'dateType-' + index"
                              :id="'dateType-current-' + index"
                              v-model="rule.dateType"
                              value="current"
                            />
                            <label
                              class="form-check-label"
                              :for="'dateType-current-' + index"
                              >Date of completion</label
                            >
                          </div>
                        </div>

                        <!-- Calendar date input - fixed v-model binding -->
                        <div v-if="rule.dateType === 'calendar'" class="mt-2">
                          <input
                            type="date"
                            class="form-control form-control-sm"
                            v-model="rule.value"
                          />
                        </div>

                        <!-- Number of days input - fixed v-model binding -->
                        <div
                          v-else-if="rule.dateType === 'number'"
                          class="input-group input-group-sm mt-2"
                        >
                          <input
                            type="number"
                            class="form-control"
                            v-model="rule.daysValue"
                            placeholder="Number of days"
                          />
                          <span class="input-group-text">Days</span>
                        </div>

                        <!-- Current date option -->
                        <div
                          v-else-if="rule.dateType === 'current'"
                          class="form-text text-primary mt-2"
                        >
                          <i class="fas fa-calendar-day me-1"></i>Using today's
                          date when the form is submitted
                        </div>
                      </div>
                      <!-- For Select/Choice type questions -->
                      <div
                        v-else-if="
                          selectedQuestion.question_type === 'Selection' ||
                          selectedQuestion.question_type ===
                            'Multi Selection' ||
                          selectedQuestion.question_type === 'Checkbox'
                        "
                        class="selection-input-container"
                      >
                        <select
                          class="form-select form-select-sm"
                          v-model="rule.value"
                        >
                          <option value="">Select an option</option>
                          <option
                            v-for="option in getAvailableOptions()"
                            :key="option.id"
                            :value="option"
                            :disabled="isOptionDisabled(option, index)"
                          >
                            {{ option.option }}
                          </option>
                        </select>
                      </div>

                      <!-- For File type questions - specific file validators -->
                      <div
                        v-else-if="selectedQuestion.question_type === 'File'"
                        class="row g-2"
                      >
                        <div class="col-5">
                          <select
                            class="form-select form-select-sm"
                            v-model="rule.fileProperty"
                          >
                            <option value="extension">File Extension</option>
                            <option value="size">File Size (KB)</option>
                          </select>
                        </div>
                        <div class="col-7">
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="rule.value"
                            :placeholder="
                              rule.fileProperty === 'extension'
                                ? 'pdf,doc,jpg'
                                : 'Max size in KB'
                            "
                          />
                        </div>
                      </div>

                      <!-- For Boolean type questions -->
                      <div
                        v-else-if="selectedQuestion.question_type === 'Boolean'"
                        class="bg-white p-2 rounded border"
                      >
                        <div class="form-check form-check-inline mb-0">
                          <input
                            class="form-check-input"
                            type="radio"
                            :name="'bool-' + index"
                            id="bool-true-${index}"
                            v-model="rule.value"
                            value="true"
                          />
                          <label
                            class="form-check-label"
                            for="bool-true-${index}"
                            >True</label
                          >
                        </div>
                        <div class="form-check form-check-inline mb-0">
                          <input
                            class="form-check-input"
                            type="radio"
                            :name="'bool-' + index"
                            id="bool-false-${index}"
                            v-model="rule.value"
                            value="false"
                          />
                          <label
                            class="form-check-label"
                            for="bool-false-${index}"
                            >False</label
                          >
                        </div>
                      </div>

                      <!-- Default text input for other question types -->
                      <input
                        v-else
                        type="text"
                        class="form-control form-control-sm"
                        v-model="rule.value"
                        placeholder="Enter comparison value"
                      />
                    </div>

                    <!-- Score input with weight limit and percentage display - only show if weight exists and > 0 -->
                    <div
                      v-if="
                        selectedQuestion.weight && selectedQuestion.weight > 0
                      "
                      class="col-md-4"
                    >
                      <label class="form-label fw-semibold mb-2">
                        <i class="fas fa-star me-1"></i>Score (0-{{
                          selectedQuestion.weight
                        }})
                      </label>
                      <div class="input-group input-group-sm">
                        <input
                          type="number"
                          class="form-control form-control-sm"
                          v-model.number="rule.score"
                          min="0"
                          :max="selectedQuestion.weight"
                          placeholder="score"
                          @input="clampScore(rule)"
                        />
                        <span class="input-group-text">
                          {{
                            (
                              (Math.min(rule.score, selectedQuestion.weight) /
                                selectedQuestion.weight) *
                              100
                            ).toFixed(1)
                          }}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Rules Message - Improved styling -->
              <div v-else class="text-center py-5 bg-light rounded">
                <i class="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                <p class="text-muted mb-2">No rules configured yet.</p>
                <button
                  class="btn bg-green rounded-small btn-sm me-2 text-white text-xxs"
                  @click="addNewRule"
                >
                  <i class="fas fa-plus me-1"></i>Add Your First Rule
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer with improved buttons -->
        <div class="modal-footer bg-light">
          <button
            type="button"
            class="btn bg-dark rounded-small btn-sm me-2 text-white text-xxs"
            @click="closeConfigModal"
          >
            <i class="fas fa-times me-1"></i>Cancel
          </button>
          <button
            type="button"
            class="btn bg-green rounded-small btn-sm me-2 text-white text-xxs"
            @click="saveRules"
          >
            <i class="fas fa-save me-1"></i>Save Rules
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Weight Setting Modal -->
  <div
    v-if="showWeightModal"
    class="modal-backdrop fade show"
    @click="closeWeightModal"
  ></div>
  <div
    v-if="showWeightModal"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-labelledby="weightModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-md">
      <div class="modal-content" @click.stop>
        <div class="modal-header bg-dark text-white">
          <h5 class="modal-title" id="weightModalLabel">Set Question Score</h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            @click="closeWeightModal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <p class="mb-3">{{ weightQuestion.question }}</p>

          <div class="mb-3 d-flex align-items-center">
            <label class="form-label mb-0 me-3">Score:</label>
            <input
              type="number"
              class="form-control w-25"
              v-model.number="weightQuestion.weight"
              min="0"
              max="999999"
              step="1"
            />
          </div>
        </div>
        <div class="modal-footer d-flex">
          <button
            type="button"
            class="btn bg-danger rounded-small btn-md me-2 text-white text-xs"
            @click="closeWeightModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn bg-green rounded-small btn-md me-2 text-white text-xs"
            @click="saveScore"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script src="./manage_form.js"></script>
